import React, { useState, useEffect, useCallback } from "react";
import {
  Container, Grid, Paper, Typography, Button, TextField, Card, CardContent,
  CardActions, IconButton, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, Select, InputLabel, FormControl, makeStyles
} from "@material-ui/core";
import { Add, Edit, Delete, DragIndicator, AttachMoney } from "@material-ui/icons";
import {
  getDealStages, createDealStage, updateDealStage, deleteDealStage,
  getDeals, createDeal, updateDeal, deleteDeal
} from "../../services/api.new-features";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  stageColumn: {
    minWidth: 280, maxWidth: 320, padding: theme.spacing(1),
    backgroundColor: "#f5f5f5", borderRadius: 8, margin: theme.spacing(0.5),
    maxHeight: "calc(100vh - 200px)", overflowY: "auto"
  },
  stageHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    marginBottom: theme.spacing(1), padding: theme.spacing(1)
  },
  dealCard: {
    marginBottom: theme.spacing(1), cursor: "pointer",
    "&:hover": { boxShadow: theme.shadows[4] }
  },
  pipelineContainer: {
    display: "flex", overflowX: "auto", padding: theme.spacing(1), gap: theme.spacing(1)
  },
  valueChip: { backgroundColor: "#4caf50", color: "#fff" },
}));

const CRM = () => {
  const classes = useStyles();
  const [stages, setStages] = useState([]);
  const [deals, setDeals] = useState([]);
  const [dealDialog, setDealDialog] = useState(false);
  const [stageDialog, setStageDialog] = useState(false);
  const [currentDeal, setCurrentDeal] = useState({});
  const [currentStage, setCurrentStage] = useState({});

  const loadData = useCallback(async () => {
    try {
      const [stagesRes, dealsRes] = await Promise.all([getDealStages(), getDeals({})]);
      setStages(stagesRes.data);
      setDeals(dealsRes.data.deals || dealsRes.data);
    } catch (e) { toast.error("Erro ao carregar CRM"); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSaveDeal = async () => {
    try {
      if (currentDeal.id) { await updateDeal(currentDeal.id, currentDeal); }
      else { await createDeal(currentDeal); }
      setDealDialog(false);
      setCurrentDeal({});
      loadData();
      toast.success("Negócio salvo!");
    } catch (e) { toast.error("Erro ao salvar negócio"); }
  };

  const handleDeleteDeal = async (id) => {
    if (!window.confirm("Excluir negócio?")) return;
    try { await deleteDeal(id); loadData(); toast.success("Excluído!"); }
    catch (e) { toast.error("Erro"); }
  };

  const handleSaveStage = async () => {
    try {
      if (currentStage.id) { await updateDealStage(currentStage.id, currentStage); }
      else { await createDealStage(currentStage); }
      setStageDialog(false);
      setCurrentStage({});
      loadData();
    } catch (e) { toast.error("Erro ao salvar etapa"); }
  };

  const handleDeleteStage = async (id) => {
    if (!window.confirm("Excluir etapa?")) return;
    try { await deleteDealStage(id); loadData(); }
    catch (e) { toast.error("Erro"); }
  };

  const moveDeal = async (deal, newStageId) => {
    try { await updateDeal(deal.id, { stageId: newStageId }); loadData(); }
    catch (e) { toast.error("Erro ao mover"); }
  };

  return (
    <Container maxWidth={false} className={classes.root}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Typography variant="h5">CRM - Pipeline de Negócios</Typography>
        <div>
          <Button variant="outlined" color="primary" onClick={() => { setCurrentStage({}); setStageDialog(true); }}
            style={{ marginRight: 8 }}>+ Etapa</Button>
          <Button variant="contained" color="primary" onClick={() => { setCurrentDeal({}); setDealDialog(true); }}>
            + Negócio</Button>
        </div>
      </Grid>

      <div className={classes.pipelineContainer}>
        {stages.map((stage) => (
          <Paper key={stage.id} className={classes.stageColumn} elevation={1}>
            <div className={classes.stageHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: stage.color }} />
                <Typography variant="subtitle1"><b>{stage.name}</b></Typography>
                <Chip size="small" label={
                  (deals.filter(d => d.stageId === stage.id) || []).length
                } />
              </div>
              <div>
                <IconButton size="small" onClick={() => { setCurrentStage(stage); setStageDialog(true); }}>
                  <Edit fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => handleDeleteStage(stage.id)}>
                  <Delete fontSize="small" /></IconButton>
              </div>
            </div>
            {deals.filter(d => d.stageId === stage.id).map((deal) => (
              <Card key={deal.id} className={classes.dealCard}>
                <CardContent style={{ paddingBottom: 8 }}>
                  <Typography variant="subtitle2">{deal.title}</Typography>
                  {deal.contact && <Typography variant="caption" color="textSecondary">{deal.contact.name}</Typography>}
                  {deal.value > 0 && (
                    <Chip size="small" icon={<AttachMoney />} label={`R$ ${Number(deal.value).toLocaleString("pt-BR")}`}
                      className={classes.valueChip} style={{ marginTop: 4 }} />
                  )}
                </CardContent>
                <CardActions style={{ padding: "0 8px 8px" }}>
                  <IconButton size="small" onClick={() => { setCurrentDeal(deal); setDealDialog(true); }}>
                    <Edit fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => handleDeleteDeal(deal.id)}>
                    <Delete fontSize="small" /></IconButton>
                  {stages.filter(s => s.id !== stage.id).slice(0, 2).map(s => (
                    <Chip key={s.id} size="small" label={s.name} clickable onClick={() => moveDeal(deal, s.id)}
                      style={{ marginLeft: 4 }} />
                  ))}
                </CardActions>
              </Card>
            ))}
          </Paper>
        ))}
      </div>

      {/* Deal Dialog */}
      <Dialog open={dealDialog} onClose={() => setDealDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{currentDeal.id ? "Editar Negócio" : "Novo Negócio"}</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="dense" label="Título" value={currentDeal.title || ""}
            onChange={e => setCurrentDeal({ ...currentDeal, title: e.target.value })} />
          <TextField fullWidth margin="dense" label="Valor (R$)" type="number" value={currentDeal.value || ""}
            onChange={e => setCurrentDeal({ ...currentDeal, value: e.target.value })} />
          <FormControl fullWidth margin="dense">
            <InputLabel>Etapa</InputLabel>
            <Select value={currentDeal.stageId || ""} onChange={e => setCurrentDeal({ ...currentDeal, stageId: e.target.value })}>
              {stages.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth margin="dense" label="Fonte" value={currentDeal.source || ""}
            onChange={e => setCurrentDeal({ ...currentDeal, source: e.target.value })} />
          <TextField fullWidth margin="dense" label="Previsão de Fechamento" type="date"
            InputLabelProps={{ shrink: true }} value={currentDeal.expectedCloseDate || ""}
            onChange={e => setCurrentDeal({ ...currentDeal, expectedCloseDate: e.target.value })} />
          <TextField fullWidth margin="dense" label="Observações" multiline rows={3}
            value={currentDeal.notes || ""} onChange={e => setCurrentDeal({ ...currentDeal, notes: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDealDialog(false)}>Cancelar</Button>
          <Button onClick={handleSaveDeal} variant="contained" color="primary">Salvar</Button>
        </DialogActions>
      </Dialog>

      {/* Stage Dialog */}
      <Dialog open={stageDialog} onClose={() => setStageDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{currentStage.id ? "Editar Etapa" : "Nova Etapa"}</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="dense" label="Nome" value={currentStage.name || ""}
            onChange={e => setCurrentStage({ ...currentStage, name: e.target.value })} />
          <TextField fullWidth margin="dense" label="Cor" type="color" value={currentStage.color || "#6366f1"}
            onChange={e => setCurrentStage({ ...currentStage, color: e.target.value })} />
          <TextField fullWidth margin="dense" label="Ordem" type="number" value={currentStage.order || 0}
            onChange={e => setCurrentStage({ ...currentStage, order: Number(e.target.value) })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStageDialog(false)}>Cancelar</Button>
          <Button onClick={handleSaveStage} variant="contained" color="primary">Salvar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CRM;